/**
 * Notification Aggregation Logic
 * Groups notifications by their linked entity (chat, booking, user, robot)
 */

import type { NotificationItem, NotificationType } from "./service";

export type AggregatedNotification = {
  // Aggregation key (e.g., "chat-abc123")
  groupKey: string;
  // Type of entity (chat, booking, user, robot)
  entityType: NotificationType;
  // ID of the linked entity
  entityId: string | null;
  // All notifications in this group
  notifications: NotificationItem[];
  // Count of notifications
  count: number;
  // Most recent notification (used for display)
  latest: NotificationItem;
  // Severity of the group (highest severity in the group)
  severity: NotificationItem["severity"];
  // Whether this group is expanded
  isExpanded: boolean;
};

/**
 * Get the grouping key for a notification
 * For notifications with links, group by link ID
 * For notifications without links, each is its own group
 */
const getGroupKey = (item: NotificationItem): string => {
  if (item.link && item.link.id) {
    return `${item.link.kind}-${item.link.id}`;
  }
  // No link - each notification is its own group
  return `orphan-${item.id}`;
};

/**
 * Determine the highest severity from a list
 */
const getHighestSeverity = (notifications: NotificationItem[]): NotificationItem["severity"] => {
  const severityOrder: Record<NotificationItem["severity"], number> = {
    critical: 4,
    warning: 3,
    success: 2,
    info: 1
  };

  return notifications.reduce((highest, item) => {
    return severityOrder[item.severity] > severityOrder[highest]
      ? item.severity
      : highest;
  }, "info" as NotificationItem["severity"]);
};

/**
 * Aggregate notifications by their linked entity
 */
export const aggregateNotifications = (
  notifications: NotificationItem[]
): AggregatedNotification[] => {
  // Group notifications by their key
  const groupMap = new Map<string, NotificationItem[]>();

  notifications.forEach(item => {
    const key = getGroupKey(item);
    const existing = groupMap.get(key) || [];
    existing.push(item);
    groupMap.set(key, existing);
  });

  // Convert to aggregated format
  const aggregated: AggregatedNotification[] = [];

  groupMap.forEach((items, groupKey) => {
    // Sort by most recent first
    const sorted = items.sort((a, b) => {
      const aTime = a.createdAt?.getTime() || 0;
      const bTime = b.createdAt?.getTime() || 0;
      return bTime - aTime;
    });

    const latest = sorted[0];
    const entityId = latest.link?.id || null;
    const entityType = latest.type;

    aggregated.push({
      groupKey,
      entityType,
      entityId,
      notifications: sorted,
      count: sorted.length,
      latest,
      severity: getHighestSeverity(sorted),
      isExpanded: false
    });
  });

  // Sort aggregated groups by most recent notification
  return aggregated.sort((a, b) => {
    const aTime = a.latest.createdAt?.getTime() || 0;
    const bTime = b.latest.createdAt?.getTime() || 0;
    return bTime - aTime;
  });
};

/**
 * Get a display title for an aggregated group
 */
export const getAggregatedTitle = (agg: AggregatedNotification): string => {
  if (agg.count === 1) {
    return agg.latest.title;
  }

  // Multiple notifications - show count
  const entityType = agg.latest.link?.kind || agg.entityType;
  return `${agg.count} notifications for this ${entityType}`;
};

/**
 * Get a display message for an aggregated group
 */
export const getAggregatedMessage = (agg: AggregatedNotification): string => {
  if (agg.count === 1) {
    return agg.latest.message;
  }

  // Show the latest message + indicator of more
  return `${agg.latest.message} (+ ${agg.count - 1} more)`;
};
